"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { ECON_CHART_STRINGS } from "@/components/tools/econ-micro/chart-strings";
import {
  GroupTable,
  IncomeStats,
  IncomeWorking,
  LorenzGraph,
} from "@/components/tools/econ-micro/gini-view";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  detectDelimiter,
  parseCell,
  sourceLines,
  splitCells,
} from "@/lib/tool-kit/charts/parse";
import {
  type Group,
  giniOf,
  giniOfGroups,
  lorenzOf,
} from "@/lib/tool-kit/econ-micro/inequality";

/** English strings. A translated content file overrides these through `ui`. */
export const GINI_COEFFICIENT_CALCULATOR_STRINGS = {
  ...ECON_CHART_STRINGS,
  modeLabel: "Your data are",
  modeIncomes: "Individual incomes, one per person or household",
  modeGroups: "Grouped shares, such as quintiles",
  incomesLabel: "Incomes — numbers separated by commas, spaces or new lines",
  incomesHint:
    "Use semicolons if your numbers have decimal commas. Incomes must be 0 or more.",
  exampleIncomes: "12, 18, 25, 32, 40, 55, 70, 95, 130, 220",
  groupsLabel: "Groups — one per line: population share, income share",
  groupsHint:
    "Shares in percent. A line with one number is an equal-sized group with that income share, so five lines describe quintiles. Groups are sorted by income per person.",
  exampleGroups: "5\n10\n15\n25\n45",
  results: "Results",
  tooFew: "Enter at least two incomes.",
  negative:
    "Incomes must be 0 or more. Negative values, such as net wealth in debt, can push the coefficient above 1 and are not supported.",
  zeroTotal: "The incomes add up to zero, so shares cannot be worked out.",
  groupsTooFew: "Enter at least two groups.",
  skippedLines: "Lines {lines} have no readable number and were left out.",
  listJoin: ", ",
  statGini: "Gini coefficient",
  statCorrected: "Small-sample Gini, G × n ÷ (n − 1)",
  statMaximum: "Highest possible for this many people",
  statMean: "Mean income",
  statCount: "People counted",
  statGroupedGini: "Gini coefficient (grouped data)",
  statGroupedPercent: "As a percentage",
  workingHeading: "Working",
  workSorted: "Sorted incomes: {values}",
  workWeighted:
    "Σ i × income₍ᵢ₎ = {sum}, with the incomes sorted from smallest to largest",
  workGini: "G = 2 × {sum} ÷ ({n} × {total}) − ({n} + 1) ÷ {n} = {gini}",
  workCorrected: "Small-sample version: {gini} × {n} ÷ ({n} − 1) = {corrected}",
  workNote:
    "This equals the average gap between every pair of incomes divided by twice the mean. The small-sample version rescales the maximum to 1, for data that are a sample of a larger population.",
  groupedNote:
    "The groups are joined by straight lines on the Lorenz curve, so inequality inside each group is ignored and the coefficient is a lower bound on the true one.",
  reordered: "The groups were sorted from poorest to richest per person.",
  tableHeading: "Lorenz curve points",
  columnPopulation: "Population share (%)",
  columnIncome: "Income share (%)",
  columnCumPop: "Cumulative population (%)",
  columnCumIncome: "Cumulative income (%)",
  graphTitle: "Lorenz curve",
  axisPeople: "Cumulative share of people (%)",
  axisIncome: "Cumulative share of income (%)",
  curveEquality: "Equality",
  curveLorenz: "Lorenz curve",
  legendArea: "Area A: the gap that the Gini measures",
  graphDescription:
    "Lorenz curve with cumulative share of people on the horizontal axis and cumulative share of income on the vertical axis, below the dashed line of perfect equality. The Gini coefficient, the shaded gap as a share of the area under the line of equality, is {gini}.",
} as const;

export type GiniCoefficientStrings = typeof GINI_COEFFICIENT_CALCULATOR_STRINGS;
type Mode = "incomes" | "groups";

/** Groups from `population, income` lines; one number is an equal-sized group. */
function readGroups(raw: string) {
  const lines = sourceLines(raw);
  const delimiter = detectDelimiter(lines.map((l) => l.text));
  const numbers = lines.map((line) => ({
    line: line.line,
    values: splitCells(line.text, delimiter)
      .map(parseCell)
      .filter((v): v is number => v !== null),
  }));
  const skipped = numbers
    .filter((l) => l.values.length === 0)
    .map((l) => l.line);
  const used = numbers.filter((l) => l.values.length > 0);
  const groups: Group[] = used.map((l) =>
    l.values.length === 1
      ? { population: 100 / used.length, income: l.values[0] }
      : { population: l.values[0], income: l.values[1] }
  );
  return { groups, skipped };
}

function GroupedResult({
  raw,
  strings: s,
  fmt,
}: {
  raw: string;
  strings: GiniCoefficientStrings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const locale = useToolLocale();
  const { groups, skipped } = readGroups(raw);
  const result = giniOfGroups(groups);
  if (typeof result === "string") {
    const messages = {
      "too-few": s.groupsTooFew,
      negative: s.negative,
      "zero-total": s.zeroTotal,
    };
    return <ErrorNotice>{messages[result]}</ErrorNotice>;
  }
  const n = (v: number) => fmt(v, { maximumFractionDigits: 2 });
  const rows = result.lorenz.slice(1).map((point, i) => {
    const before = result.lorenz[i];
    return [
      n(point[0] - before[0]),
      n(point[1] - before[1]),
      n(point[0]),
      n(point[1]),
    ];
  });
  return (
    <div className="space-y-5">
      {skipped.length > 0 ? (
        <Notice>
          {tPlural(s.skippedLines, locale, skipped.length, {
            lines: skipped.map((l) => fmt(l)).join(s.listJoin),
          })}
        </Notice>
      ) : null}
      {result.reordered ? <Notice>{s.reordered}</Notice> : null}
      <StatGrid>
        <StatCard
          emphasis
          label={s.statGroupedGini}
          value={fmt(result.gini, { maximumFractionDigits: 4 })}
        />
        <StatCard
          label={s.statGroupedPercent}
          value={fmt(result.gini, {
            style: "percent",
            maximumFractionDigits: 2,
          })}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">{s.groupedNote}</p>
      <LorenzGraph
        fmt={fmt}
        gini={result.gini}
        points={result.lorenz}
        strings={s}
      />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.tableHeading}</h3>
        <GroupTable rows={rows} strings={s} />
      </div>
    </div>
  );
}

function IncomeResult({
  raw,
  strings: s,
  fmt,
}: {
  raw: string;
  strings: GiniCoefficientStrings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const values = parseNumberList(raw);
  const result = giniOf(values);
  if (typeof result === "string") {
    const messages = {
      "too-few": s.tooFew,
      negative: s.negative,
      "zero-total": s.zeroTotal,
    };
    return <ErrorNotice>{messages[result]}</ErrorNotice>;
  }
  return (
    <div className="space-y-5">
      <IncomeStats fmt={fmt} result={result} strings={s} />
      <IncomeWorking fmt={fmt} result={result} strings={s} />
      <LorenzGraph
        fmt={fmt}
        gini={result.gini}
        points={lorenzOf(values)}
        strings={s}
      />
    </div>
  );
}

export default function GiniCoefficientCalculatorWidget() {
  const strings = useToolUi(GINI_COEFFICIENT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("incomes");
  const [incomes, setIncomes] = useState<string>(strings.exampleIncomes);
  const [groups, setGroups] = useState<string>(strings.exampleGroups);
  const grouped = mode === "groups";

  return (
    <div className="space-y-6">
      <SelectField
        id="gc-mode"
        label={strings.modeLabel}
        onChange={setMode}
        options={[
          { value: "incomes", label: strings.modeIncomes },
          { value: "groups", label: strings.modeGroups },
        ]}
        value={mode}
      />
      <TextAreaField
        hint={grouped ? strings.groupsHint : strings.incomesHint}
        id="gc-data"
        label={grouped ? strings.groupsLabel : strings.incomesLabel}
        onChange={grouped ? setGroups : setIncomes}
        rows={6}
        value={grouped ? groups : incomes}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {grouped ? (
          <GroupedResult fmt={fmt} raw={groups} strings={strings} />
        ) : (
          <IncomeResult fmt={fmt} raw={incomes} strings={strings} />
        )}
      </section>
    </div>
  );
}
